import * as React from 'react';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { useUser } from '../../../../hooks/useUser';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';

export const useAssetTransfer = (props: {
    onTransferSuccess: () => void;
    onTransferFailure: () => void;
}) => {
    const { onTransferFailure, onTransferSuccess } = props;
    const [transferInitiated, setTransferInitiated] = React.useState(false);

    const { api } = useAppConfigContext();
    const invokingUser = useUser();
    const [transferOptions, setTransferOptions] = React.useState<
        | {
              sourceUserId: string;
              target: {
                  transferTargetType: string;
                  target: string;
              };
          }
        | undefined
    >(undefined);

    const transferAssets = React.useMemo(
        () =>
            (props: {
                fromUser: User;
                target: { userId: string } | { group: string };
            }) => {
                const targetData =
                    'userId' in props.target
                        ? {
                              transferTargetType: 'USER',
                              target: props.target.userId,
                          }
                        : {
                              transferTargetType: 'GROUP',
                              target: props.target.group,
                          };

                setTransferOptions({
                    sourceUserId: props.fromUser.userId,
                    target: targetData,
                });
                setTransferInitiated(true);
            },
        [],
    );

    const transferAssetsApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: `${api!.quicksightDashboards}/v1/data/dashboards/tenants/${invokingUser?.tenantIdentifier}/users/${encodeURIComponent(transferOptions?.sourceUserId || '')}/assets/transfer`,
            useAuth: true,
            executeType: 'manual',
            method: 'POST',
            data: { target: transferOptions?.target },
        }),
        [api, invokingUser, transferOptions],
    );

    const { data, error, execute, loading, reset } = useApi(
        transferAssetsApiConfig,
    );

    React.useEffect(() => {
        if (transferInitiated) {
            execute();
        }
    }, [execute, transferInitiated]);

    React.useEffect(() => {
        if (data && !loading && !error) {
            onTransferSuccess();
            reset();
            setTransferInitiated(false);
            setTransferOptions(undefined);
        }
    }, [data, error, loading, onTransferSuccess, reset]);

    React.useEffect(() => {
        if (error && !loading) {
            onTransferFailure();
            reset();
            setTransferInitiated(false);
            setTransferOptions(undefined);
        }
    }, [error, loading, onTransferFailure, reset]);

    return { transferAssets, transferInProgress: transferInitiated };
};
